/**
 * Date and time utility functions using native JavaScript APIs
 * - For timezone conversion operations:
 *   - Use `dayjs` wrapped in helper functions here in this file. (e.g. `getGMTOffset`)
 *   - This due to lack of proper timezone support in `date-fns` v2 & v3, and we are blocked from upgrading to v4 until we upgrade `react-day-picker`.
 *   - Wrapping `dayjs` in helper functions here would allow us to easily migrate away from `dayjs` when we're able to do so.
 * - For specific and common formatting and parsing ops from/to DB format:
 *   - Use `date-fns` functions wrapped in helper functions here in this file. (e.g. `parseUTCDate`)
 * - For basic datetime formatting, parsing and comparison operations that doesn't involve timezone conversion:
 *   - Use `date-fns` functions directly in the components. (e.g. `isBefore`, `isAfter`, `formatDistanceToNow`, etc.)
 */

import { parseISO, sub, add } from 'date-fns';

import dayjs from 'utils/extendedDayJS';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { isValidIanaTimezone, FALLBACK_TIMEZONE } from 'utils/timezoneValidation';

/**
 * Parse a UTC date string from the database
 * Database sends UTC datetime strings without the 'Z' suffix, so we need to add it
 * to ensure parseISO interprets it as UTC rather than local time
 *
 * @param utcDateString - UTC date string from database (e.g., '2024-01-15 10:30:00')
 * @returns Date object representing the UTC time
 *
 * @example
 * // Database sends: '2024-01-15 10:30:00'
 * // This function converts it to: '2024-01-15 10:30:00Z'
 * // Then parseISO correctly interprets it as UTC
 * const date = parseUTCDate('2024-01-15 10:30:00');
 */
export const parseUTCDate = (utcDateString: string): Date => {
  return parseISO(`${utcDateString}Z`);
};

/**
 * Get GMT offset for a given timezone
 * @param timezoneCode - IANA timezone code (e.g., 'America/New_York')
 * @returns GMT offset string (e.g., 'GMT-5' or 'GMT+0')
 */
export const getGMTOffset = (timezoneCode: string): string => {
  return dayjs.getGMTOffset(timezoneCode);
};

/**
 * Convert a datetime to a timezone
 * @param datetime - Datetime to convert
 * @param timezoneCode - IANA timezone code to convert to
 * @returns Date object representing the timezone time
 */
export const convertToTimezone = (datetime: Date | string, timezoneCode: string): Date => {
  return dayjs.convertToTimezone(datetime, timezoneCode).toDate();
};

/**
 * Guess the timezone from the browser, falling back to UTC when the browser
 * returns something that isn't a valid IANA identifier (e.g. a raw offset like
 * '+00:00'), which every consumer and the API would otherwise reject
 * @returns IANA timezone code (e.g., 'America/New_York')
 */
export const guessTimezone = (): string => {
  const guessed = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return isValidIanaTimezone(guessed) ? guessed : FALLBACK_TIMEZONE;
};

/**
 * Interpret a Date object as being in a specific timezone and convert to UTC
 * @param date - Date object to interpret in the given timezone
 * @param timezone - IANA timezone code (e.g., 'America/New_York')
 * @returns Date object representing the UTC time
 */
export const convertDateToUtc = (date: Date, timezone: string): Date => {
  // Format the date without timezone info to preserve local time components
  const dateTimeString = dayjs(date).format(FULL_DATE_FORMAT);

  // Parse this as if it's in the specified timezone, then convert to UTC
  const dayjsInTimezone = dayjs.tz(dateTimeString, timezone);

  // Convert to UTC and return as Date
  return dayjsInTimezone.utc().toDate();
};

/**
 * Format a date in a specific timezone using a format string
 * @param date - Date object to format
 * @param timezone - IANA timezone code (e.g., 'America/New_York')
 * @param format - Format string (e.g., 'yyyy-MM-dd HH:mm:ss')
 * @returns Formatted date string
 */
export const formatDateInTimezone = (date: Date, timezone: string, format: string): string => {
  return dayjs(date).tz(timezone).format(format);
};

/**
 * Get the start of day in a specific timezone
 * @param timezone - IANA timezone code to convert to
 * @param utcDateString - Optional UTC date string. If not provided, defaults to current UTC time
 * @returns Date object representing the start of day in the given timezone
 */
export const getStartOfDayInTimezone = (timezone: string, utcDateString?: string): Date => {
  const dateToUse = utcDateString || dayjs.utc().toISOString();
  return dayjs.utc(dateToUse).tz(timezone).startOf('day').toDate();
};

/**
 * Convert a calendar date to a specific timezone (wall-clock time remains same)
 * @param date - Date object from calendar picker
 * @param timezone - IANA timezone code to interpret the date in
 * @returns Date object representing the date interpreted in the given timezone
 */
export const interpretDateInTimezone = (date: Date, timezone: string): Date => {
  const wallClockDate = dayjs(date).format('YYYY-MM-DD');
  return dayjs.tz(`${wallClockDate} 00:00`, 'YYYY-MM-DD HH:mm', timezone).toDate();
};

/**
 * Calculate absolute time from a relative schedule based on event datetime
 * @param eventDateTime - Date object (typically from parseUTCDate)
 * @param quantity - Number of time units
 * @param unit - Time unit (minutes, hours, days)
 * @param beforeEventBegins - Whether to subtract (true) or add (false) from eventDateTime
 * @returns Date object representing the calculated time
 */
export const calculateScheduleTime = (
  eventDateTime: Date,
  quantity: number,
  unit: string,
  beforeEventBegins: boolean
): Date => {
  // Map unit string to date-fns duration
  const duration = { [unit.toLowerCase()]: quantity };

  // Add or subtract based on beforeEventBegins
  return beforeEventBegins ? sub(eventDateTime, duration) : add(eventDateTime, duration);
};
